import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { Guest, Table } from '@guabi/shared';
import {
  ROOM,
  SEAT_R,
  TAP_SLOP_PX,
  autoArrange,
  clampToRoom,
  clientToRoom,
  footprint,
  isPlaced,
  overlaps,
  seatPositions,
  snap,
} from './floorGeometry';

/**
 * The room, drawn.
 *
 * The dumb renderer over `floorGeometry.ts` — the same split as `Logo.tsx` over
 * `logoGeometry.ts`, and the only one available: this repo has no jsdom, so
 * every decision worth testing had to leave the component.
 *
 * **The `viewBox` is the room**, so one user unit is one room unit and the
 * browser does all the scaling. No pixel arithmetic, no resize listener, and it
 * fits a phone and a laptop by construction. `vector-effect="non-scaling-stroke"`
 * is what stops the hairlines thickening as the room shrinks.
 */

interface Props {
  tables: Table[];
  bySeat: Map<string, Guest[]>;
  selectedId?: string;
  onSelect: (tableId: string | undefined) => void;
  onMove: (tableId: string, x: number, y: number) => void;
}

interface Drag {
  tableId: string;
  pointerId: number;
  /** Centre minus finger, so the table does not jump to be under the thumb. */
  grabX: number;
  grabY: number;
  x: number;
  y: number;
  startClientX: number;
  startClientY: number;
  travel: number;
  rect: DOMRect;
}

export function FloorPlan({ tables, bySeat, selectedId, onSelect, onMove }: Props) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<Drag>();

  /**
   * Tables nobody has placed are laid out rather than left in a heap at the
   * origin — `x`/`y` default to 0, so a new room would otherwise open with every
   * table stacked in the corner, which reads as broken rather than empty.
   */
  const placed = useMemo(() => {
    const unplaced = tables.filter((table) => !isPlaced(table));
    if (unplaced.length === 0) return tables;
    const spots = autoArrange(unplaced);
    return tables.map((table) => {
      const at = spots.get(table.tableId);
      return at ? { ...table, x: at.x, y: at.y } : table;
    });
  }, [tables]);

  const live = placed.map((table) =>
    drag?.tableId === table.tableId ? { ...table, x: drag.x, y: drag.y } : table,
  );

  const clashing = useMemo(() => {
    const out = new Set<string>();
    const prints = live.map((table) => ({ id: table.tableId, f: footprint(table) }));
    for (let i = 0; i < prints.length; i += 1) {
      for (let j = i + 1; j < prints.length; j += 1) {
        const a = prints[i];
        const b = prints[j];
        if (a && b && overlaps(a.f, b.f)) {
          out.add(a.id);
          out.add(b.id);
        }
      }
    }
    return out;
  }, [live]);

  const onPointerDown = (e: ReactPointerEvent<SVGGElement>, table: Table) => {
    // A second finger must not start a second drag.
    if (!e.isPrimary || !svgRef.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = svgRef.current.getBoundingClientRect();
    const at = clientToRoom(rect, e.clientX, e.clientY);
    setDrag({
      tableId: table.tableId,
      pointerId: e.pointerId,
      grabX: (table.x ?? 0) - at.x,
      grabY: (table.y ?? 0) - at.y,
      x: table.x ?? 0,
      y: table.y ?? 0,
      startClientX: e.clientX,
      startClientY: e.clientY,
      travel: 0,
      rect,
    });
  };

  const onPointerMove = (e: ReactPointerEvent<SVGGElement>, table: Table) => {
    if (!drag || drag.pointerId !== e.pointerId) return;
    const at = clientToRoom(drag.rect, e.clientX, e.clientY);
    const next = clampToRoom(footprint(table), snap(at.x + drag.grabX), snap(at.y + drag.grabY));
    setDrag({
      ...drag,
      ...next,
      travel: Math.hypot(e.clientX - drag.startClientX, e.clientY - drag.startClientY),
    });
  };

  const onPointerUp = (e: ReactPointerEvent<SVGGElement>, table: Table) => {
    if (!drag || drag.pointerId !== e.pointerId) return;
    const moved = drag.travel >= TAP_SLOP_PX;
    setDrag(undefined);
    // One gesture, two meanings: a still finger selects, a travelling one moves.
    // Without this a 40px table would need a separate drag handle to miss.
    if (!moved) {
      onSelect(selectedId === table.tableId ? undefined : table.tableId);
      return;
    }
    if (drag.x !== (table.x ?? 0) || drag.y !== (table.y ?? 0)) {
      onMove(table.tableId, drag.x, drag.y);
    }
  };

  const nudge = (e: React.KeyboardEvent<SVGGElement>, table: Table) => {
    const step = e.shiftKey ? 4 : 1;
    const by: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(selectedId === table.tableId ? undefined : table.tableId);
      return;
    }
    const delta = by[e.key];
    if (!delta) return;
    e.preventDefault();
    const at = clampToRoom(footprint(table), (table.x ?? 0) + delta[0], (table.y ?? 0) + delta[1]);
    onMove(table.tableId, at.x, at.y);
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${ROOM.width} ${ROOM.depth}`}
      style={{ aspectRatio: `${ROOM.width} / ${ROOM.depth}` }}
      className="panel h-auto w-full select-none bg-paper"
      role="group"
      aria-label={t('seating.planLabel')}
    >
      {live.map((table) => {
        const f = footprint(table);
        const seats = seatPositions(table);
        const taken = bySeat.get(table.tableId)?.length ?? 0;
        const selected = selectedId === table.tableId;
        const clash = clashing.has(table.tableId);
        const stroke = clash ? '#8A3D2E' : selected ? '#B4914F' : '#E4DECE';

        return (
          <g
            key={table.tableId}
            // `touch-action: none` sits on the table, never the whole canvas —
            // on the canvas it would be a dead zone a thumb cannot scroll past.
            style={{ touchAction: 'none', WebkitTouchCallout: 'none' }}
            className="cursor-grab focus:outline-none"
            tabIndex={0}
            role="button"
            aria-label={t('seating.tableLabel', {
              name: table.name,
              taken,
              capacity: table.capacity,
            })}
            aria-pressed={selected}
            onPointerDown={(e) => onPointerDown(e, table)}
            onPointerMove={(e) => onPointerMove(e, table)}
            onPointerUp={(e) => onPointerUp(e, table)}
            // iOS cancels the pointer for its own gestures; without this the
            // table stays glued to the finger for ever.
            onPointerCancel={() => setDrag(undefined)}
            onContextMenu={(e) => e.preventDefault()}
            onKeyDown={(e) => nudge(e, table)}
          >
            <title>{`${table.name} — ${String(taken)}/${String(table.capacity)}`}</title>

            {seats.map((seat, i) => (
              <circle
                key={`${table.tableId}-${String(i)}`}
                cx={table.x + seat.x}
                cy={table.y + seat.y}
                r={SEAT_R}
                // Taken seats filled, empty ones left open. This ring is the
                // whole feature: a glance says table four has two spare.
                //
                // Empty seats are outlined in sage, not the line colour — on
                // paper, line on cream is so quiet that an empty table read as
                // no table at all, which is the opposite of what it should say.
                fill={i < taken ? '#1E4034' : '#FDFBF6'}
                stroke={i < taken ? '#1E4034' : '#A8BCAF'}
                strokeWidth={0.5}
                vectorEffect="non-scaling-stroke"
              />
            ))}

            {f.size.round ? (
              <circle
                cx={table.x}
                cy={table.y}
                r={f.size.w / 2}
                fill="#F6F2E9"
                stroke={stroke}
                strokeWidth={selected || clash ? 1.4 : 0.8}
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <rect
                x={table.x - f.size.w / 2}
                y={table.y - f.size.h / 2}
                width={f.size.w}
                height={f.size.h}
                rx={0.6}
                fill="#F6F2E9"
                stroke={stroke}
                strokeWidth={selected || clash ? 1.4 : 0.8}
                vectorEffect="non-scaling-stroke"
                transform={`rotate(${String(f.deg)} ${String(table.x)} ${String(table.y)})`}
              />
            )}

            {/* A 40px table cannot carry a name. The number always fits, and the
                name lives in the card below. */}
            <text
              x={table.x}
              y={table.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={3}
              fill="#15201B"
              className="font-display"
            >
              {shortLabel(table.name)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * What fits inside a table: a number if the name has one, else its initials.
 *
 * "Mesa de los primos de Nicole" in a shape 40px across is a smudge; "12" is
 * readable at any size this plan is ever drawn at.
 */
export function shortLabel(name: string): string {
  const digits = /\d+/.exec(name);
  if (digits) return digits[0];
  return name
    .split(/\s+/)
    .filter((w) => w.length > 2)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}
